import { getVisibleBeads } from "@pindou/game-core";
import type { GameState, LevelDefinition, Point, VisibleBead } from "@pindou/game-core";
import { beadPalette } from "./colors";

const LOGICAL_SIZE = 600;
const PADDING = 18;

function pointKey(point: Point): string {
  return `${point.x},${point.y}`;
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

export class BoardRenderer {
  private readonly context: CanvasRenderingContext2D;
  private readonly cellSize: number;

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly level: LevelDefinition,
  ) {
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D context unavailable");
    }
    this.context = context;
    this.cellSize = (LOGICAL_SIZE - PADDING * 2) / level.board.width;
  }

  public draw(state: GameState, highlighted: ReadonlySet<string> = new Set()): void {
    this.syncCanvasSize();
    const context = this.context;
    context.clearRect(0, 0, LOGICAL_SIZE, LOGICAL_SIZE);
    this.drawBoardFrame();

    const lit = new Set(state.litTargetSlotIds);
    const targets = new Map<string, typeof this.level.targetSlots>();
    for (const slot of this.level.targetSlots) {
      const key = pointKey(slot);
      targets.set(key, [...(targets.get(key) ?? []), slot]);
    }

    for (let y = 0; y < this.level.board.height; y += 1) {
      for (let x = 0; x < this.level.board.width; x += 1) {
        this.drawBaseCell({ x, y }, targets.get(`${x},${y}`) ?? [], lit);
      }
    }

    for (const bead of getVisibleBeads(this.level.board, state)) {
      this.drawBead(bead, highlighted.has(bead.beadId));
    }
  }

  public pointFromPointer(event: PointerEvent): Point | null {
    const rect = this.canvas.getBoundingClientRect();
    const scale = LOGICAL_SIZE / rect.width;
    const logicalX = (event.clientX - rect.left) * scale;
    const logicalY = (event.clientY - rect.top) * scale;
    const x = Math.floor((logicalX - PADDING) / this.cellSize);
    const y = Math.floor((logicalY - PADDING) / this.cellSize);
    if (x < 0 || y < 0 || x >= this.level.board.width || y >= this.level.board.height) {
      return null;
    }
    return { x, y };
  }

  public cellCenterClient(point: Point): DOMPoint {
    const rect = this.canvas.getBoundingClientRect();
    const ratio = rect.width / LOGICAL_SIZE;
    return new DOMPoint(
      rect.left + (PADDING + (point.x + 0.5) * this.cellSize) * ratio,
      rect.top + (PADDING + (point.y + 0.5) * this.cellSize) * ratio,
    );
  }

  private syncCanvasSize(): void {
    const cssSize = Math.max(1, Math.round(this.canvas.clientWidth));
    const density = Math.min(window.devicePixelRatio || 1, 2);
    const pixels = Math.round(cssSize * density);
    if (this.canvas.width !== pixels || this.canvas.height !== pixels) {
      this.canvas.width = pixels;
      this.canvas.height = pixels;
    }
    this.context.setTransform(pixels / LOGICAL_SIZE, 0, 0, pixels / LOGICAL_SIZE, 0, 0);
    this.context.imageSmoothingEnabled = true;
  }

  private drawBoardFrame(): void {
    const context = this.context;
    context.save();
    context.shadowColor = "rgba(71, 65, 126, .25)";
    context.shadowBlur = 16;
    context.shadowOffsetY = 8;
    roundedRect(context, 5, 5, 590, 590, 22);
    context.fillStyle = "#f8f5df";
    context.fill();
    context.shadowColor = "transparent";
    context.lineWidth = 7;
    context.strokeStyle = "#b8b4ae";
    context.stroke();
    roundedRect(context, 13, 13, 574, 574, 15);
    context.lineWidth = 4;
    context.strokeStyle = "#fffdf2";
    context.stroke();
    context.restore();
  }

  private drawBaseCell(
    point: Point,
    slots: typeof this.level.targetSlots,
    lit: ReadonlySet<string>,
  ): void {
    const x = PADDING + point.x * this.cellSize;
    const y = PADDING + point.y * this.cellSize;
    const context = this.context;
    const litSlot = [...slots].reverse().find((slot) => lit.has(slot.id));
    const slot = litSlot ?? slots[0];
    if (!slot) {
      const gap = 2.5;
      roundedRect(context, x + gap, y + gap, this.cellSize - gap * 2, this.cellSize - gap * 2, 5);
      context.fillStyle = "#e7e0c7";
      context.fill();
      context.lineWidth = 1.2;
      context.strokeStyle = "rgba(151,143,117,.28)";
      context.stroke();
      return;
    }

    const gap = 0.8;
    const size = this.cellSize - gap * 2;
    const palette = beadPalette(slot.color);
    roundedRect(context, x + gap, y + gap, size, size, 4);
    context.fillStyle = palette.guide;
    context.fill();
    context.lineWidth = 2.2;
    context.strokeStyle = palette.edge;
    context.stroke();

    roundedRect(context, x + gap + 3, y + gap + 3, size - 6, size - 6, 3);
    context.lineWidth = 1.4;
    context.strokeStyle = "rgba(255,255,255,.38)";
    context.stroke();

    if (litSlot) {
      this.drawGemAt(point, slot.color, 0.82, false);
    }
  }

  private drawBead(bead: VisibleBead, highlighted: boolean): void {
    this.drawGemAt(bead, bead.color, highlighted ? 0.86 : 0.78, highlighted);
  }

  private drawGemAt(point: Point, color: string, scale: number, highlighted: boolean): void {
    const palette = beadPalette(color);
    const centerX = PADDING + (point.x + 0.5) * this.cellSize;
    const centerY = PADDING + (point.y + 0.5) * this.cellSize;
    const size = this.cellSize * scale;
    const x = centerX - size / 2;
    const y = centerY - size / 2 - (highlighted ? 2.5 : 0);
    const context = this.context;
    const gradient = context.createLinearGradient(x, y, x + size, y + size);
    gradient.addColorStop(0, palette.top);
    gradient.addColorStop(0.52, palette.bottom);
    gradient.addColorStop(1, palette.edge);

    context.save();
    context.shadowColor = highlighted ? "rgba(255,255,255,.85)" : palette.shadow;
    context.shadowBlur = highlighted ? 12 : 3;
    context.shadowOffsetY = highlighted ? 0 : 2.5;
    roundedRect(context, x, y, size, size, Math.max(5, size * 0.2));
    context.fillStyle = gradient;
    context.fill();
    context.shadowColor = "transparent";
    context.lineWidth = highlighted ? 3 : 1.7;
    context.strokeStyle = highlighted ? "#fffbe9" : palette.edge;
    context.stroke();
    context.beginPath();
    context.moveTo(x + size * 0.18, y + size * 0.18);
    context.lineTo(x + size * 0.72, y + size * 0.12);
    context.lineTo(x + size * 0.33, y + size * 0.43);
    context.closePath();
    context.fillStyle = "rgba(255,255,255,.22)";
    context.fill();
    context.restore();
  }
}
